import { useState, useEffect, useMemo } from "react";
import { useAuth, ROLES_FULL_ACCESS } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import axios from "axios";
import { API } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import {
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Loader2,
  Trash2,
  Pencil
} from "lucide-react";

const getInitials = (name) => {
  const parts = (name || "").split(" ");
  return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
};

const AbsencesPage = () => {
  const { user } = useAuth();
  const [absences, setAbsences] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New absence form state
  const [absenceType, setAbsenceType] = useState("");
  const [absenceDate, setAbsenceDate] = useState(null);
  const [duration, setDuration] = useState("8");
  const [reason, setReason] = useState("");

  // Edit absence state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editAbsence, setEditAbsence] = useState(null);
  const [editAbsenceType, setEditAbsenceType] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editDuration, setEditDuration] = useState("8");
  const [editReason, setEditReason] = useState("");
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const usersMap = useMemo(
    () => Object.fromEntries(allUsers.map((u) => [u.id, u])),
    [allUsers]
  );

  useEffect(() => {
    fetchAbsences();
    if (!["employee", "collaborateur"].includes(user?.role)) {
      axios.get(`${API}/users`).then((r) => setAllUsers(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAbsences = async () => {
    try {
      const response = await axios.get(`${API}/absences`);
      setAbsences(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des absences");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!absenceDate || !absenceType) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/absences`, {
        absence_type: absenceType,
        date: format(absenceDate, "yyyy-MM-dd"),
        duration_hours: parseFloat(duration),
        reason: reason
      });
      toast.success("Absence déclarée avec succès !");
      setIsDialogOpen(false);
      resetForm();
      fetchAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la déclaration");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setAbsenceType("");
    setAbsenceDate(null);
    setDuration("8");
    setReason("");
  };

  const handleDelete = async (absenceId) => {
    if (!window.confirm("Supprimer cette absence ?")) return;
    try {
      await axios.delete(`${API}/absences/${absenceId}`);
      toast.success("Absence supprimée");
      fetchAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleEditOpen = (absence) => {
    setEditAbsence(absence);
    setEditAbsenceType(absence.absence_type);
    setEditDate(absence.date);
    setEditDuration(String(absence.duration_hours));
    setEditReason(absence.reason || "");
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editAbsence) return;
    setIsEditSubmitting(true);
    try {
      await axios.put(`${API}/absences/${editAbsence.id}`, {
        absence_type: editAbsenceType,
        date: editDate,
        duration_hours: parseFloat(editDuration),
        reason: editReason
      });
      toast.success("Absence modifiée avec succès !");
      setIsEditDialogOpen(false);
      fetchAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const canDelete = (absence) => {
    if (ROLES_FULL_ACCESS.includes(user?.role)) return true;
    return absence.user_id === user?.id;
  };

  const canEdit = () => ROLES_FULL_ACCESS.includes(user?.role);

  const getAbsenceTypeLabel = (type) => {
    const labels = {
      maladie: "Maladie",
      retard: "Retard",
      autre: "Autre"
    };
    return labels[type] || type;
  };

  const getAbsenceTypeBadge = (type) => {
    const config = {
      maladie: { className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700/50" },
      retard: { className: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700/50" },
      autre: { className: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-[#112240] dark:text-[#7EA8CE] dark:border-[#1A3050]" }
    };
    const { className } = config[type] || config.autre;
    return <Badge className={`${className} border`}>{getAbsenceTypeLabel(type)}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] dark:text-[#4B8BF4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="absences-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Gestion des absences
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base">
            Déclarez vos absences et consultez l'historique
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal={true}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] rounded-full"
              data-testid="new-absence-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Déclarer une absence
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto dark:bg-[#0B1628] dark:border-[#1A3050]">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
                Déclarer une absence
              </DialogTitle>
              <DialogDescription className="dark:text-[#7EA8CE]">
                Remplissez le formulaire pour déclarer une absence.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="absenceType" className="dark:text-[#D6E4F5]">Type d'absence *</Label>
                <Select value={absenceType} onValueChange={setAbsenceType}>
                  <SelectTrigger data-testid="absence-type-select" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maladie">Maladie</SelectItem>
                    <SelectItem value="retard">Retard</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="dark:text-[#D6E4F5]">Date *</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                      data-testid="absence-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {absenceDate ? format(absenceDate, "dd/MM/yyyy") : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <Calendar
                      mode="single"
                      selected={absenceDate}
                      onSelect={setAbsenceDate}
                      locale={fr}
                      autoFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration" className="dark:text-[#D6E4F5]">Durée (heures)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.5"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="absence-duration"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason" className="dark:text-[#D6E4F5]">Motif (optionnel)</Label>
                <Textarea
                  id="reason"
                  placeholder="Précisez le motif de votre absence..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="absence-reason"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d]"
                  disabled={isSubmitting}
                  data-testid="submit-absence"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Déclarer"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} modal={true}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto dark:bg-[#0B1628] dark:border-[#1A3050]">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
              Modifier l'absence
            </DialogTitle>
            <DialogDescription className="dark:text-[#7EA8CE]">
              Modifiez les informations de l'absence de {editAbsence?.user_name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Type d'absence</Label>
              <Select value={editAbsenceType} onValueChange={setEditAbsenceType}>
                <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="maladie">Maladie</SelectItem>
                  <SelectItem value="retard">Retard</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Date</Label>
              <Input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>

            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Durée (heures)</Label>
              <Input
                type="number"
                min="0.5"
                max="24"
                step="0.5"
                value={editDuration}
                onChange={(e) => setEditDuration(e.target.value)}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>

            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Motif</Label>
              <Textarea
                placeholder="Motif..."
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d]"
                disabled={isEditSubmitting}
              >
                {isEditSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Absences Table */}
      <Card className="card-neumorphic">
        <CardContent className="p-0">
          {absences.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF] dark:text-[#3D607E]">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune absence déclarée</p>
            </div>
          ) : (
            <>
            {/* Mobile : cartes */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-[#1A3050]">
              {absences.map((absence) => (
                <div key={absence.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      {user?.role !== "employee" && user?.role !== "collaborateur" && (() => {
                        const u = usersMap[absence.user_id];
                        return (
                          <Avatar className="w-7 h-7 shrink-0 mt-0.5">
                            <AvatarImage src={u?.avatar_url} />
                            <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]">
                              {getInitials(absence.user_name)}
                            </AvatarFallback>
                          </Avatar>
                        );
                      })()}
                      <div className="min-w-0">
                        {user?.role !== "employee" && user?.role !== "collaborateur" && (
                          <p className="font-medium text-[#111827] dark:text-[#D6E4F5] text-sm"><ClickableUser userId={absence.user_id}>{absence.user_name}</ClickableUser></p>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {getAbsenceTypeBadge(absence.absence_type)}
                          <span className="text-xs text-[#4B5563] dark:text-[#7EA8CE]">{absence.date} · {absence.duration_hours}h</span>
                        </div>
                        {absence.reason && (
                          <p className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] mt-1 line-clamp-1">{absence.reason}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {canEdit() && (
                        <Button size="icon" variant="ghost"
                          className="h-8 w-8 text-blue-600 dark:text-[#4B8BF4] hover:bg-blue-50 dark:hover:bg-[#0D1E3A]"
                          onClick={() => handleEditOpen(absence)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete(absence) && (
                        <Button size="icon" variant="ghost"
                          className="h-8 w-8 text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          onClick={() => handleDelete(absence.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop : tableau */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="dark:border-[#1A3050]">
                    {user?.role !== "employee" && user?.role !== "collaborateur" && (
                      <TableHead className="dark:text-[#7EA8CE]">Collaborateur</TableHead>
                    )}
                    <TableHead className="dark:text-[#7EA8CE]">Type</TableHead>
                    <TableHead className="dark:text-[#7EA8CE]">Date</TableHead>
                    <TableHead className="dark:text-[#7EA8CE]">Durée</TableHead>
                    <TableHead className="dark:text-[#7EA8CE]">Motif</TableHead>
                    <TableHead className="text-right dark:text-[#7EA8CE]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {absences.map((absence) => (
                    <TableRow key={absence.id} className="dark:border-[#1A3050] dark:hover:bg-[#112240]/50">
                      {user?.role !== "employee" && user?.role !== "collaborateur" && (() => {
                        const u = usersMap[absence.user_id];
                        return (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="w-7 h-7 shrink-0">
                                <AvatarImage src={u?.avatar_url} />
                                <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]">
                                  {getInitials(absence.user_name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium dark:text-[#D6E4F5] whitespace-nowrap"><ClickableUser userId={absence.user_id}>{absence.user_name}</ClickableUser></span>
                            </div>
                          </TableCell>
                        );
                      })()}
                      <TableCell>{getAbsenceTypeBadge(absence.absence_type)}</TableCell>
                      <TableCell className="dark:text-[#D6E4F5]">{absence.date}</TableCell>
                      <TableCell className="dark:text-[#D6E4F5]">{absence.duration_hours}h</TableCell>
                      <TableCell className="max-w-[200px] truncate dark:text-[#7EA8CE]">
                        {absence.reason || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canEdit() && (
                            <Button size="icon" variant="ghost"
                              className="h-8 w-8 text-blue-600 dark:text-[#4B8BF4] hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-[#0D1E3A]"
                              onClick={() => handleEditOpen(absence)} title="Modifier">
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete(absence) && (
                            <Button size="icon" variant="ghost"
                              className="h-8 w-8 text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => handleDelete(absence.id)} title="Supprimer">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AbsencesPage;
